// Acesso e papéis customizáveis: o que cada papel PODE (/v1/access/me,
// /v1/access/catalog) e os papéis por marca (/v1/roles). Domínio separado de
// ./users.js de propósito — lá é a lista de membros (feature `users` do back),
// aqui são as features `access` e `roles`. Nenhum dos dois importa o outro.
import { api, toApiError } from "./client.js";

// Espelha as features `access` e `roles` do back (/v1/access/*, /v1/roles).
// Substitui os três papéis fixos por papéis por marca, orientados por um
// catálogo de permissões (chave `<módulo>.<tela>.<ação>`).

export type BaseRole = "director" | "manager" | "seller";

// GET /v1/access/me — quem está logado, com que papel e quais permissões tem.
// Não é gateada por permissão nenhuma no back: é justamente onde o front
// aprende o que ele pode.
export interface AccessMe {
  role: { id: string | null; name: string; baseRole: BaseRole; isSystem: boolean };
  permissions: string[];
  platformDirector: boolean;
  // Qualquer membro de uma company grupo pode criar outra marca. É separado
  // de `crossBrand`, que exige também o flag de diretor de plataforma.
  canCreateBrand: boolean;
  // Cross-marca — o consolidado de todas as marcas e a listagem de marca alheia
  // — já resolvido pelo back: é o par (flag de plataforma, cliente do tipo
  // 'grupo'). Vem pronto, e não como o tipo do cliente, porque quem decide é o
  // mesmo predicado que barra a rota (allowsCrossBrand, no back): reimplementar
  // a regra aqui faria a tela e o endpoint divergirem no dia em que ela mudar.
  crossBrand: boolean;
  // Passo obrigatório do primeiro acesso: a pessoa já nomeou a empresa e
  // escolheu o tipo de cliente? Desde o cadastro transacional a marca nasce
  // JUNTO com a conta (com o nome da pessoa), então "tem marca?" deixou de
  // distinguir quem terminou o cadastro — este carimbo é quem distingue.
  companyOnboarded: boolean;
}

export async function getAccessMe(): Promise<AccessMe> {
  const { data, error } = await api<AccessMe>("/v1/access/me");
  if (error) throw toApiError("GET /v1/access/me", error);
  return data;
}

// GET /v1/access/catalog — o catálogo de permissões usado para renderizar o
// editor de papéis. `screens` é indexado pela chave da tela dentro do módulo.
export interface CatalogScreen {
  name: string;
  route: string;
  minRole: BaseRole;
  actions: Record<string, { name: string }>;
}

export interface CatalogModule {
  name: string;
  screens: Record<string, CatalogScreen>;
}

export type PermissionCatalog = Record<string, CatalogModule>;

export async function getPermissionCatalog(): Promise<PermissionCatalog> {
  const { data, error } = await api<PermissionCatalog>("/v1/access/catalog");
  if (error) throw toApiError("GET /v1/access/catalog", error);
  return data;
}

// Papel customizável de uma marca (GET /v1/roles, POST/PATCH/DELETE /v1/roles).
// Nome diferente de `OrgRole` (./users.js) de propósito: `OrgRole` já é o tipo do
// papel FIXO (director|manager|seller) usado em todo o resto do app — as
// telas que ainda dependem dele são reescritas só em tasks futuras desta
// mesma feature. `CustomRole` é o registro do papel por marca que vem de
// /v1/roles, com id e permissões próprias.
export interface CustomRole {
  id: string;
  name: string;
  baseRole: BaseRole;
  permissions: string[];
  isSystem: boolean;
  memberCount: number;
}

export interface RoleInput {
  name: string;
  baseRole: BaseRole;
  permissions: string[];
}

// GET /v1/roles — todos os papéis da marca ativa.
export async function listRoles(): Promise<CustomRole[]> {
  const { data, error } = await api<{ roles: CustomRole[] }>("/v1/roles");
  if (error) throw toApiError("GET /v1/roles", error);
  return data.roles;
}

// POST /v1/roles — cria um papel custom (nasce sempre is_system=false).
export async function createRole(input: RoleInput): Promise<CustomRole> {
  const { data, error } = await api<CustomRole>("/v1/roles", { method: "POST", body: input });
  if (error) throw toApiError("POST /v1/roles", error);
  return data;
}

// PATCH /v1/roles/:id — recusado (409) para papéis de sistema.
export async function updateRole(id: string, input: RoleInput): Promise<CustomRole> {
  const { data, error } = await api<CustomRole>(`/v1/roles/${id}`, {
    method: "PATCH",
    body: input,
  });
  if (error) throw toApiError(`PATCH /v1/roles/${id}`, error);
  return data;
}

// DELETE /v1/roles/:id — soft delete; recusado (409) se algum membro ativo
// ainda usa o papel.
export async function deleteRole(id: string): Promise<void> {
  const { error } = await api(`/v1/roles/${id}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/roles/${id}`, error);
}
